import { DeviceTicketType, Sort } from '@/enums/api'
import { MyBasePoint, RegionArea } from '@/types/mapTypes'
import * as apiType from '@/types/api'
import qs from 'querystring'
import { v2 as client } from '@/apis/client'
import { RelocationZone } from '@/types/bike'

interface UserIdParam {
  userId: number
}

interface RegionIdParam {
  regionId: number
}

export interface UserIdAndRegionIdParam {
  user_id: number
  region_id: number
}

export interface ChangeMyDefaultBasePointsParam {
  user_id: number
  region_id: number
  base_point_id: number
}

export interface MyRegionsResponse {
  id: number
}

interface DeviceIdParam {
  deviceId: string
}

export interface RegionResponse {
  area: RegionArea
  center: Location
  id: number
  landing_bottom_right: Location
  landing_top_left: Location
  name: string
}

export interface BasePoint {
  id: number
  name: string
  type: string // TODO: basePoint type enum
  maintenance_process_count: number
  relocation_pickup_count: number
  basePoints?: BasePoint[]
  region_id: number
}

export interface DevicesCount {
  total: number
  can_use: number
  date: string
  field: number
  lost: number
  dead: number
  low_battery: number
  need_repair: number
  need_repair_available: number
  need_repair_unavailable: number
  region: number
  warehouse: number
  relocation_picked: number
  riding: number
  current_available_devices: number
  yesterday_available_devices: number
  last_week_available_devices: number
  current_complete_unique_devices: number
  yesterday_complete_unique_devices: number
  last_week_complete_unique_devices: number
}

export const getMe = async (): Promise<any> => {
  const { data } = await client.get(`/operating/me?t=${Date.now()}`)
  return data
}

export const getUser = async ({ userId }: UserIdParam): Promise<any> => {
  const { data } = await client.get(`/operating/users/${userId}?t=${Date.now()}`)
  return data
}

export const getRegionDevices = async ({ regionId }: RegionIdParam): Promise<any> => {
  const { data } = await client.get(`/operating/regions/${regionId}/devices?t=${Date.now()}`)
  return data
}

export const getRepairTickets = async ({ regionId }: RegionIdParam): Promise<any> => {
  const { data } = await client.get(`/operating/regions/${regionId}/tickets/maintenances?t=${Date.now()}`)
  return data
}

export const getRelocationTickets = async ({ regionId }: RegionIdParam): Promise<any> => {
  const { data } = await client.get(`/operating/regions/${regionId}/tickets/relocations?t=${Date.now()}`)
  return data
}

export const getBatteryTickets = async ({ regionId }: RegionIdParam): Promise<any> => {
  const { data } = await client.get(`/operating/regions/${regionId}/tickets/batteries?t=${Date.now()}`)
  return data
}

export const getParkingInfo = async ({ deviceId }: DeviceIdParam): Promise<apiType.GetParkingInfoResult> => {
  const { data } = await client.get(`/operating/devices/${deviceId}/parking/images?t=${Date.now()}`)
  return data
}

export interface RegionMapResponse {
  area: RegionArea
  center: Location
  id: number
  landing_bottom_right: Location
  landing_top_left: Location
  name: string

  basePoints?: MyBasePoint[]
}
export const getRegions = async (): Promise<Array<RegionResponse>> => {
  const { data } = await client.get(`/operating/regions?t=${Date.now()}`)
  return data
}

export const getMyRegions = async (userId: number): Promise<Array<MyRegionsResponse>> => {
  const { data } = await client.get(`/operating/users/${userId}/regions?t=${Date.now()}`)
  return data
}

export const getMyBasePoints = async (params: UserIdAndRegionIdParam): Promise<Array<BasePoint>> => {
  const { data } = await client.get(
    `/operating/users/${params.user_id}/base_points?region_id=${params.region_id}&t=${Date.now()}`
  )
  return data
}

export const changeMyDefaultRegion = async (params: UserIdAndRegionIdParam) => {
  const { data } = await client.put(`/operating/users/${params.user_id}/default_region`, params)
  return data
}

export const changeMyDefaultBasePoints = async (params: ChangeMyDefaultBasePointsParam) => {
  const { data } = await client.put(
    `/operating/users/${params.user_id}/regions/${params.region_id}/default_base_point`,
    params
  )
  return data
}

export const getDevicesCountByRegion = async (regionId: number): Promise<DevicesCount> => {
  const { data } = await client.get(`/operating/regions/${regionId}/devices/count?t=${Date.now()}`)
  return data
}

export const fixComment = async (commentId: number) => {
  const { data } = await client.put('/operating/maintenances/comment_fix', {
    device_maintenance_comment_id: commentId,
  })
  return data
}

export const unfixComment = async (commentId: number) => {
  const { data } = await client.put('/operating/maintenances/comment_unfix', {
    device_maintenance_comment_id: commentId,
  })
  return data
}

export const getDeviceParts = async () => {
  const { data } = await client.get(`/operating/device_parts?t=${Date.now()}`)
  return data
}

export const getDevice = async ({ deviceId }: { deviceId: string }) => {
  const { data } = await client.get(`/operating/devices/${deviceId}?t=${Date.now()}`)
  return data
}

export const getRepairTicket = async ({ deviceId }: { deviceId: string }) => {
  const { data } = await client.get(`/operating/maintenances?device_id=${deviceId}&t=${Date.now()}`)
  return data
}

export const getRelocationTicket = async ({ deviceId }: { deviceId: string }) => {
  const { data } = await client.get(`/operating/relocations?device_id=${deviceId}&t=${Date.now()}`)
  return data
}

export const getBatteryTicket = async ({ deviceId }: { deviceId: string }) => {
  const { data } = await client.get(`/operating/batteries?device_id=${deviceId}&t=${Date.now()}`)
  return data
}

export const changeMaintenanceBasePoint = async (req: any): Promise<any> => {
  return (await client.put<any>(`/operating/maintenances/${req.maintenanceId}/base_point`, req)).data
}

export const changeRelocationBasePoint = async (req: any): Promise<any> => {
  return (await client.put<any>(`/operating/relocations/${req.relocationId}/base_point`, req)).data
}

export const getBringInDevices = async ({
  device_ticket_type,
  region_id,
  base_point_id,
  limit,
  sort,
  last_device_id,
}: {
  transactionId?: string
  device_ticket_type: DeviceTicketType
  region_id: number
  base_point_id?: number
  limit: number
  sort?: Sort
  last_device_id?: string
}) => {
  const { data } = await client.get<any>(
    `/operating/devices?${qs.stringify({
      device_ticket_type,
      region_id,
      base_point_id,
      limit,
      sort,
      last_device_id,
      t: Date.now(),
    })}`
  )
  return data
}

export const getRelocationZone = async ({ regionId }: RegionIdParam): Promise<any> => {
  const { data } = await client.get(`/operating/regions/${regionId}/relocation-zones`)
  return data
}

export const getRelocationZoneDetail = async (bikeZoneId: number): Promise<any> => {
  const { data } = await client.get(`/operating/bike_zones/${bikeZoneId}`)
  return data
}

export const getTripAnalysis = async (deviceId: string, createdAt: string): Promise<any> => {
  const { data } = await client.get(`/operating/devices/${deviceId}/trip_analysis?created_at=${createdAt}`)
  return data
}
